import { persistor } from '@/setup/redux/Store';
import { setSidebarState } from '@/__core/hooks/useSidebarState';
import { clearSelectedCompany } from '../helpers/CompanyStorage';

const AUTH_STORAGE_KEYS = [
  'selected_company',
  'remember_company',
  'rememberToken',
  'rememberUser',
  'persist:v100-xbox-auth',
  'access_token',
  'sessionToken',
  'sessionUser',
] as const;

const isUserSessionStorageKey = (key: string): boolean => {
  if ((AUTH_STORAGE_KEYS as readonly string[]).includes(key)) {
    return true;
  }

  if (key.startsWith('persist:')) {
    return true;
  }

  if (key.includes('_module_tabs') || key.includes('_tabs')) {
    return true;
  }

  if (key.startsWith('inventory_module_tabs_')) {
    return true;
  }

  if (key.startsWith('transaction_sequence_')) {
    return true;
  }

  if (key.endsWith('FiltersAccordionState')) {
    return true;
  }

  return false;
};

const removeMatchingKeys = (storage: Storage): void => {
  const keysToRemove: string[] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key && isUserSessionStorageKey(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
};

const clearAuthTokens = (): void => {
  localStorage.removeItem('rememberToken');
  localStorage.removeItem('rememberUser');
  sessionStorage.removeItem('sessionToken');
  sessionStorage.removeItem('sessionUser');
  localStorage.removeItem('access_token');
  sessionStorage.removeItem('access_token');
};

const clearExplicitAuthKeys = (): void => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const clearWindowModuleCaches = (): void => {
  const globalWindow = window as unknown as Record<string, unknown>;

  Object.keys(globalWindow).forEach((key) => {
    if (key.startsWith('__')) {
      delete globalWindow[key];
    }
  });
};

/**
 * Clears all persisted auth and per-user UI state (tabs, company, tokens, redux-persist).
 */
export const clearUserSession = async (): Promise<void> => {
  clearSelectedCompany();
  clearAuthTokens();
  clearExplicitAuthKeys();
  removeMatchingKeys(localStorage);
  removeMatchingKeys(sessionStorage);
  clearWindowModuleCaches();
  setSidebarState(true);

  try {
    await persistor.purge();
  } catch (error) {
    console.warn('Failed to purge persisted auth state:', error);
  }

  // redux-persist may write after purge; remove again after flush
  clearExplicitAuthKeys();
  removeMatchingKeys(localStorage);
  removeMatchingKeys(sessionStorage);
};
